// Google Ads (gtag.js) bridge. The tag itself is loaded statically in
// index.html / shopify.html; this module only pushes events and never throws.
//
// Conversion actions in Google Ads need a per-action label
// (AW-18484826983/<label>). Labels are build-time config, not code:
//   VITE_GADS_CONVERSION_LABELS='{"sign_up":"AbCd...","purchase":"EfGh..."}'
// Events are ALSO sent under their standard names (sign_up, purchase, …) so
// they show up before labels exist and can be imported later.
//
// Which labels a build uses (GH-1130, see resolveConversionLabels):
//   * VITE_GADS_TEST_CONVERSION_LABELS set → ONLY those (staging points it at
//     the TEST conversion actions), whatever VITE_ENV says;
//   * production build (VITE_ENV=production) → the built-in labels below plus
//     VITE_GADS_CONVERSION_LABELS overrides (unchanged behaviour);
//   * any other build (local, CI, PR previews, staging without test labels)
//     → only VITE_GADS_CONVERSION_LABELS, never the production labels, so
//     test traffic cannot land in the production conversion actions.
//
// Cookie consent (GH-1134): index.html starts Google Consent Mode at 'denied'
// and updates it from the visitor's banner choice. Events fired before the
// visitor has answered are held and sent right after the answer (so an Accept
// on the same page reaches Google with granted storage), or when the page is
// hidden (so a redirect cannot lose them; they then go out under the denied
// state as cookieless pings, like every event after "Necessary only").
// The OpenAI pixel is not Consent-Mode aware, so it fires only with marketing
// consent.

import { CONSENT_EVENT, currentConsent } from './cookieConsent.js';

export const GADS_TAG_ID = 'AW-18484826983';

// Not mirrored on purpose (GH-1130): card_added / trial_activated. A trial
// start goes through trackTrialStartOnce, which dedupes it; a plain mirror
// here would send an undeduped second Subscribe conversion.
const FUNNEL_TO_GTAG = {
  signup_completed: 'sign_up',
  preview_subscribe_clicked: 'begin_checkout',
  change_plan_click: 'begin_checkout',
  renew_checkout_clicked: 'begin_checkout',
  social_connected: 'social_connected',
  onboarding_skipped: 'onboarding_skipped',
  quick_brand_created: 'brand_created',
  first_post_created: 'first_post_created',
};

export function mapFunnelEventToGtag(name) {
  return FUNNEL_TO_GTAG[name] || null;
}

// Labels of the conversion actions created in Google Ads (owner, 2026-09-30).
// Build-time env can add/override: VITE_GADS_CONVERSION_LABELS='{"sign_up":"…"}'.
export const DEFAULT_CONVERSION_LABELS = {
  sign_up: 'lkkICJWh54sdEOemoO5E',   // "Sign-up"
  begin_checkout: 'Vf_8CJuh54sdEOemoO5E', // "Begin checkout"
  subscribe: '1NEfCJih54sdEOemoO5E', // "Subscribe" — subscription/trial activated
  purchase: 'CbnUCJKh54sdEOemoO5E',  // "Purchase" — paid checkout returned
};

const LABEL_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function parseLabelMap(raw) {
  try {
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed)
      .filter(([event, label]) => event && typeof label === 'string' && LABEL_PATTERN.test(label)));
  } catch {
    return {};
  }
}

/** The {event: label} map this build may report to (see the header). Pure; `env` is import.meta.env. */
export function resolveConversionLabels(env = {}) {
  const test = env.VITE_GADS_TEST_CONVERSION_LABELS;
  if (test) return parseLabelMap(test);
  const overrides = parseLabelMap(env.VITE_GADS_CONVERSION_LABELS);
  if (env.VITE_ENV === 'production') return { ...DEFAULT_CONVERSION_LABELS, ...overrides };
  return overrides;
}

function conversionLabels() {
  try {
    return resolveConversionLabels(import.meta.env || {});
  } catch {
    return {};
  }
}

function browserWindow(deps) {
  return deps.win ?? (typeof window !== 'undefined' ? window : undefined);
}

function consentOf(deps) {
  return 'consent' in deps ? deps.consent : currentConsent(browserWindow(deps));
}

// Calls made before the banner is answered, per window. Each runs once, on
// the first 'dathent:consent' event (after index.html's listener has applied
// the gtag consent update) or on pagehide.
const held = new WeakMap();

function holdUntilConsent(win, run) {
  let queue = held.get(win);
  if (!queue) {
    queue = [];
    held.set(win, queue);
    const flush = () => {
      for (const next of queue.splice(0)) {
        try { next(); } catch { /* never throw */ }
      }
    };
    win.document.addEventListener(CONSENT_EVENT, flush);
    win.addEventListener('pagehide', flush);
  }
  queue.push(run);
}

function canHold(win) {
  return typeof win?.document?.addEventListener === 'function' && typeof win.addEventListener === 'function';
}

export function gtagEvent(name, params, deps = {}) {
  try {
    const g = deps.gtag ?? browserWindow(deps)?.gtag;
    if (typeof g !== 'function' || !name) return false;
    const send = () => {
      g('event', name, params && typeof params === 'object' ? params : {});
      const label = (deps.labels ?? conversionLabels())[name];
      if (label) {
        g('event', 'conversion', { send_to: `${GADS_TAG_ID}/${label}`, ...(params || {}) });
      }
    };
    const win = browserWindow(deps);
    if (consentOf(deps) || !canHold(win)) send();
    else holdUntilConsent(win, send);
    return true;
  } catch {
    return false;
  }
}

// Money conversions are reported once per transaction id even though the
// billing tab re-renders and the URL is only stripped after the first paint.
// transaction_id is mandatory (Google Ads dedupes recurring payments without it).
function trackMoneyOnce(eventName, transactionId, params, deps) {
  const id = String(transactionId || 'checkout').slice(0, 128);
  const key = `dathent.gads.${eventName}.${id}`;
  try {
    const store = deps.storage ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
    if (store && store.getItem(key)) return false;
    const sent = gtagEvent(eventName, { ...(params || {}), transaction_id: id }, deps);
    if (sent && store) store.setItem(key, '1');
    return sent;
  } catch {
    return false;
  }
}

// OpenAI ads pixel (base script is loaded via Google Tag Manager, owner
// 2026-10-02). Its conversion for a paid subscription is a fixed call, made
// only with marketing consent (held until the banner is answered).
export function openAiPixelSubscription(deps = {}) {
  try {
    const fire = () => {
      if (consentOf(deps)?.marketing !== true) return false;
      const q = deps.oaiq ?? browserWindow(deps)?.oaiq;
      if (typeof q !== 'function') return false;
      q('measure', 'subscription_created', { type: 'plan_enrollment' });
      return true;
    };
    const win = browserWindow(deps);
    if (consentOf(deps) || !canHold(win)) return fire();
    holdUntilConsent(win, fire);
    return true;
  } catch {
    return false;
  }
}

/** Paid subscription (Stripe Checkout / renewal) → "Subscribe" conversion, value from the invoice. */
export function trackSubscribeOnce(transactionId, params = {}, deps = {}) {
  const sent = trackMoneyOnce('subscribe', transactionId, params, deps);
  if (sent) openAiPixelSubscription(deps);
  return sent;
}

/** One-off payment (e.g. credit top-up) → "Purchase" conversion. */
export function trackPurchaseOnce(transactionId, params = {}, deps = {}) {
  return trackMoneyOnce('purchase', transactionId, params, deps);
}

/** {amountPaid (cents), currency} of the newest paid invoice, or null. */
export function pickInvoiceValue(invoices) {
  try {
    const paid = (Array.isArray(invoices) ? invoices : [])
      .filter((inv) => inv && (inv.status === 'paid' || (inv.amountPaid ?? 0) > 0) && (inv.amountPaid ?? 0) > 0)
      .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    const inv = paid[0];
    if (!inv) return null;
    return { value: Math.round(inv.amountPaid) / 100, currency: String(inv.currency || 'EUR').toUpperCase(), invoiceId: inv.id || null };
  } catch {
    return null;
  }
}

// GH-1130: a trial started at the card wall → the "Subscribe" conversion
// ("subscription/trial activated"), once per Stripe checkout session (hosted
// checkout) or subscription id (embedded card capture). No value: the
// action's default value in Google Ads applies. It keeps its own
// localStorage key, so the Settings return's trackSubscribeOnce for the same
// session still runs exactly as before (OpenAI pixel included); both carry
// the same transaction_id, which Google Ads dedupes for one action.
export function trackTrialStartOnce(transactionId, deps = {}) {
  try {
    if (!transactionId) return false;
    const id = String(transactionId).slice(0, 128);
    const key = `dathent.gads.trial_start.${id}`;
    let store = null;
    try { store = deps.storage ?? (typeof window !== 'undefined' ? window.localStorage : null); } catch { store = null; }
    try { if (store && store.getItem(key)) return false; } catch { /* unreadable: send; Google dedupes the id */ }
    const sent = gtagEvent('subscribe', { transaction_id: id, trial: true }, deps);
    if (sent) {
      try { store?.setItem(key, '1'); } catch { /* best effort */ }
    }
    return sent;
  } catch {
    return false;
  }
}

const SUBSCRIPTION_ID = /^sub_[A-Za-z0-9]{1,250}$/;
const CHECKOUT_SESSION_ID = /^cs_[A-Za-z0-9_]{1,250}$/;

function isTrialing(entitlements) {
  const e = entitlements?.data ?? entitlements;
  if (!e || typeof e !== 'object') return false;
  if (typeof e.accessState === 'string') return e.accessState === 'trialing';
  return e.billingStatus === 'trialing';
}

function hostedSessionId(search) {
  const sessionId = new URLSearchParams(search || '').get('session_id');
  return sessionId && CHECKOUT_SESSION_ID.test(sessionId) ? sessionId : null;
}

/**
 * The id that proves a NEW trial just started at the card wall, or null.
 * - Embedded card capture: trial/activate answered {subscriptionId, status: 'trialing'}.
 * - Hosted checkout: the return URL carries a real Stripe session id AND the
 *   entitlements read after it show the org trialing (a paid start reports
 *   nothing; followTrialStart keeps reading until the trial row lands).
 */
export function trialStartConversionId({ search = '', activation = null, entitlements = null } = {}) {
  try {
    const a = activation?.data ?? activation;
    if (a && a.status === 'trialing' && SUBSCRIPTION_ID.test(String(a.subscriptionId || ''))) return a.subscriptionId;
    if (!isTrialing(entitlements)) return null;
    return hostedSessionId(search);
  } catch {
    return null;
  }
}

/**
 * One entitlements read after a hosted-checkout return, for the trial report:
 * - 'trialing': the trial is confirmed, report it;
 * - 'pending':  the webhooks have not settled yet, read again. That covers a
 *   failed read, an org still 'new', and the checkout.session.completed
 *   mirror (billing_status 'checkout_completed'), which the entitlements
 *   package resolves to accessState 'active' until
 *   customer.subscription.created writes the trialing subscription row;
 * - 'settled':  anything else (a paid subscription, lapsed, free): no trial
 *   start to report.
 */
export function trialStartReadState(entitlements) {
  const e = entitlements?.data ?? entitlements;
  if (!e || typeof e !== 'object') return 'pending';
  if (isTrialing(e)) return 'trialing';
  if (e.billingStatus === 'checkout_completed' || e.billingStatus === 'checkout_pending') return 'pending';
  if (e.accessState === 'new') return 'pending';
  return 'settled';
}

const TRIAL_FOLLOW_ATTEMPTS = 10;
const TRIAL_FOLLOW_INTERVAL_MS = 3000;
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * GH-1130: reports a card-wall trial start once it is confirmed. The card
 * gate lets the user in on the first read that allows access; on a hosted
 * checkout that read is usually the checkout_completed mirror, before the
 * trialing subscription exists. So:
 * - the embedded activation answer, or a first read that already shows the
 *   trial, reports synchronously (before the caller flips the gate);
 * - otherwise, for a return URL with a real Stripe session id, it keeps
 *   reading in the background (`read`, every `intervalMs`, at most
 *   `attempts` times) and reports when a read shows 'trialing'. It stops
 *   silently on a settled read, on timeout, or once `isCancelled()` is true.
 * Resolves to the reported id or null. Never rejects.
 */
export async function followTrialStart({
  search = '',
  activation = null,
  first = null,
  read = async () => null,
  report = trackTrialStartOnce,
  wait = sleep,
  isCancelled = () => false,
  attempts = TRIAL_FOLLOW_ATTEMPTS,
  intervalMs = TRIAL_FOLLOW_INTERVAL_MS,
} = {}) {
  try {
    const now = trialStartConversionId({ search, activation, entitlements: first });
    if (now) {
      report(now);
      return now;
    }
    if (!hostedSessionId(search) || trialStartReadState(first) === 'settled') return null;
    for (let i = 0; i < attempts; i += 1) {
      await wait(intervalMs);
      if (isCancelled()) return null;
      let ent = null;
      try { ent = await read(); } catch { ent = null; }
      if (isCancelled()) return null;
      const state = trialStartReadState(ent);
      if (state === 'settled') return null;
      if (state === 'trialing') {
        const id = trialStartConversionId({ search, entitlements: ent });
        if (id) report(id);
        return id;
      }
    }
    return null;
  } catch {
    return null;
  }
}
